<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getPDFCoverage } from '../../../../repositories/landing.repository';
import storePolicy from '@store/policy';

const props = defineProps<{ policyId: number | null }>();
const emit = defineEmits<{ (e: 'restart'): void }>();

const { t } = useI18n();
const store = storePolicy();

const year = new Date().getFullYear();
const folio = computed(() => `${props.policyId}/${year}`);
const email = computed(() => store.data?.email ?? '');
const copiedKey = ref<string>('');

// Monto con IVA (16%)
const amount = computed(() => {
  const base = store.data?.endPrice ?? 0;
  const total = Math.round(base * 1.16);
  return '$ ' + total.toLocaleString('es-MX') + '.00';
});

const insuredAmount = computed(() =>
  '$' + Number(store.data?.indemnificationLimit ?? 0).toLocaleString('es-MX'),
);

// Vigencia: inicio y fin del viaje
const fmtDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';
const vigencia = computed(() => {
  const start = store.data?.travelStartDate;
  const end = store.data?.travelEndDate;
  return start && end ? `${fmtDate(start)} → ${fmtDate(end)}` : '—';
});

const handleDownload = () => getPDFCoverage(props.policyId);

const copy = async (text: string, key: string) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    copiedKey.value = key;
    setTimeout(() => (copiedKey.value = ''), 1600);
  } catch (_) {
    /* noop */
  }
};

const waPaid = computed(
  () =>
    `https://wa.me/525546194648?text=${encodeURIComponent(
      'Hola, ya transferí mi pago. Referencia: ' + folio.value,
    )}`,
);

// No usamos router.push('/') porque redirige al mismo /home donde ya
// estamos parados; el router no vuelve a montar el componente y el wizard
// quedaría congelado en este paso. Delegamos el reinicio a Home.vue.
const restart = () => emit('restart');

// Solo la cuenta y la CLABE se pueden copiar
const bankRows = computed(() => [
  {
    key: 'cuenta',
    label: t('landing.wizard.tu_poliza.cuenta'),
    value: t('landing.wizard.tu_poliza.cuenta_valor'),
  },
  {
    key: 'clabe',
    label: t('landing.wizard.tu_poliza.clabe'),
    value: t('landing.wizard.tu_poliza.clabe_valor'),
  },
]);
</script>

<template>
  <section class="wz-confirm">
    <div class="wz-confirm-icon">✓</div>
    <h2>{{ t('landing.paqueteria.contratacion_paqueteria.felicitaciones_poliza_generada') }}</h2>
    <div class="wz-confirm-tx">
      {{ t('landing.paqueteria.contratacion_paqueteria.numero_transaccion') }}
      <strong>{{ folio }}</strong>
    </div>

    <p class="wz-confirm-lead">
      {{ t('landing.paqueteria.contratacion_paqueteria.carta_cobertura_enviada') }}
      <template v-if="email"> ({{ email }}) </template>
      <a @click="handleDownload">{{ t('landing.paqueteria.contratacion_paqueteria.aqui') }}</a>.
    </p>

    <ol class="wz-status-timeline">
      <li class="wz-status-step wz-status-done">
        <span class="wz-status-icon">✓</span>
        <div>
          <div class="wz-status-title">
            {{ t('landing.wizard.tu_poliza.carta_emitida_titulo') }}
          </div>
          <div class="wz-status-sub">
            {{ t('landing.wizard.tu_poliza.carta_emitida_sub') }}
          </div>
        </div>
      </li>
      <li class="wz-status-step wz-status-pending">
        <span class="wz-status-icon">⏳</span>
        <div>
          <div class="wz-status-title">
            {{ t('landing.wizard.tu_poliza.poliza_def_titulo') }}
          </div>
          <div class="wz-status-sub">
            {{ t('landing.wizard.tu_poliza.poliza_def_sub') }}
          </div>
        </div>
      </li>
    </ol>

    <!-- Resumen de la solicitud -->
    <div class="wz-confirm-summary">
      <div class="row">
        <span>{{ t('landing.wizard.tu_poliza.vigencia') }}</span>
        <span>{{ vigencia }}</span>
      </div>
      <div class="row">
        <span>{{ t('landing.wizard.tu_poliza.suma_asegurada') }}</span>
        <span>{{ insuredAmount }}</span>
      </div>
      <div class="row">
        <span>{{ t('landing.wizard.tu_poliza.prima_a_pagar') }}</span>
        <span class="wz-amount-highlight">
          {{ amount }} {{ t('landing.wizard.tu_poliza.iva_incluido') }}
        </span>
      </div>
    </div>

    <div class="wz-bank">
      <div class="wz-bank-header">
        <span class="wz-bank-label">
          {{ t('landing.paqueteria.contratacion_paqueteria.datos_bancarios_titulo') }}
        </span>
        <span class="wz-bank-badge">{{ t('landing.wizard.tu_poliza.spei') }}</span>
      </div>

      <div class="wz-bank-row">
        <div class="wz-bank-row-label">
          {{ t('landing.wizard.tu_poliza.beneficiario') }}
        </div>
        <div class="wz-bank-row-value">
          <span class="mono">{{ t('landing.paqueteria.contratacion_paqueteria.banco_nombre') }}</span>
        </div>
      </div>
      <div class="wz-bank-row">
        <div class="wz-bank-row-label">
          {{ t('landing.wizard.tu_poliza.banco') }}
        </div>
        <div class="wz-bank-row-value">
          <span class="mono">{{ t('landing.paqueteria.contratacion_paqueteria.banco_entidad') }}</span>
        </div>
      </div>
      <!-- Solo Cuenta y CLABE son copiables -->
      <div v-for="row in bankRows" :key="row.key" class="wz-bank-row">
        <div class="wz-bank-row-label">{{ row.label }}</div>
        <div class="wz-bank-row-value">
          <span class="mono">{{ row.value }}</span>
          <button
            type="button"
            class="wz-copy-btn"
            :class="{ copied: copiedKey === row.key }"
            @click="copy(row.value, row.key)"
          >
            {{ copiedKey === row.key ? t('landing.wizard.tu_poliza.copiado') : t('landing.wizard.tu_poliza.copiar') }}
          </button>
        </div>
      </div>
      <div class="wz-bank-row">
        <div class="wz-bank-row-label">
          {{ t('landing.wizard.tu_poliza.referencia') }}
        </div>
        <div class="wz-bank-row-value">
          <span class="mono">{{ folio }}</span>
        </div>
      </div>
      <div class="wz-bank-row">
        <div class="wz-bank-row-label">
          {{ t('landing.wizard.tu_poliza.monto') }}
        </div>
        <div class="wz-bank-row-value">
          <span class="mono mono-strong">{{ amount }}</span>
        </div>
      </div>
    </div>

    <div class="wz-bank-warning">
      <strong>{{ t('landing.wizard.tu_poliza.atencion') }}</strong>
      {{ t('landing.paqueteria.contratacion_paqueteria.atencion_seguro_validez') }}
    </div>

    <div class="wz-confirm-actions">
      <a :href="waPaid" class="wz-cta charcoal" target="_blank">
        {{ t('landing.wizard.tu_poliza.ya_transferi') }}
      </a>
      <a href="tel:+525546194648" class="wz-cta-secondary">
        {{ t('landing.wizard.tu_poliza.dudas_pago') }} +52 55 4619 4648
      </a>
    </div>

    <button type="button" class="wz-confirm-restart" @click="restart">
      {{ t('landing.wizard.tu_poliza.cotizar_otro') }}
    </button>
  </section>
</template>
